// =============================================================================
// Reintegro de stock y combos — fuente única.
// =============================================================================
// En un combo el stock que se descuenta es el de los COMPONENTES, nunca el del
// producto combo. Por lo tanto, al revertir una operación (anulación de venta o
// devolución del cliente) reintegrar la fila del combo crea stock fantasma: un
// producto que nunca salió del depósito aparecería entrando.
//
// Esta lógica vivía privada dentro de utils/saleAnnulment.js, así que
// DevolucionNueva.jsx no la aplicaba y sí generaba ese stock fantasma. Vive acá
// para que los dos consumidores usen el mismo criterio y no vuelvan a divergir.
//
// Consumidores: utils/saleAnnulment.js (anulación de ventas) y
// pages/DevolucionNueva.jsx (devolución del cliente).

/**
 * ¿Este ítem es la fila "padre" de un combo, cuyo stock NO debe reintegrarse?
 *
 * Dos formatos conviven en la base:
 *  - Nuevo: la fila resumen del combo lleva `combo_role === 'parent'` y su stock
 *    nunca se descontó (se descontaron los componentes, que vienen como filas
 *    aparte con `combo_role === 'component'`).
 *  - Legacy: una sola fila para todo el combo. El stock descontado fue el de los
 *    ingredientes, que no están en la operación, así que no se puede
 *    reconstruir la selección. Se detecta por el `product_type` del producto.
 *
 * Ante falta de dato (`product_type` ausente o null) se asume que NO es combo:
 * es preferible reintegrar un producto normal que perder el stock de uno real.
 *
 * @param {{combo_role?: string, product_id?: string}} item
 * @param {Map<string, string>} productTypeById product_id → product_type
 * @returns {boolean}
 */
export function isComboParentItem(item, productTypeById) {
  // Formato nuevo: la fila resumen del combo lleva combo_role='parent' y su
  // stock nunca se descontó (se descontaron los componentes).
  if (String(item?.combo_role || '') === 'parent') return true;

  // Formato legacy (una sola fila para el combo): el stock descontado fue el
  // de los ingredientes, que no están en la operación. No podemos reconstruir
  // la selección, así que NO reintegramos el producto combo (reintegrarlo
  // crearía stock fantasma de un producto que nunca se descontó).
  const productType = productTypeById?.get(item?.product_id);
  return String(productType || '') === 'Combo';
}

/**
 * Cantidades a reintegrar por producto, ya filtradas y agregadas.
 *
 * Se excluyen: ítems sin `product_id`, cantidad ≤ 0 y filas padre de combo.
 * Se agrega por producto porque un combo puede repetir el mismo componente en
 * varias líneas y el stock se reintegra una sola vez por producto.
 *
 * @param {{items?: Array<{product_id?: string, quantity?: number|string, combo_role?: string}>,
 *          productTypeById?: Map<string, string>}} args
 * @returns {Map<string, number>} product_id → cantidad total a reintegrar
 */
export function buildRestorableQuantityMap(args) {
  const { items, productTypeById } = args || {};

  const quantityByProduct = new Map();

  (items || []).forEach((item) => {
    if (!item?.product_id) return;

    const quantity = Number(item.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) return;

    if (isComboParentItem(item, productTypeById)) return;

    const current = quantityByProduct.get(item.product_id) || 0;
    quantityByProduct.set(item.product_id, current + quantity);
  });

  return quantityByProduct;
}
